import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchTechnicianPatients,
  fetchServiceIdByName,
  submitTechnicianServiceDone
} from "./api";

function PatientTechnicianDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  const { packageId, technicianId, campId, packageName } = location.state || {};

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("incomplete"); // "all", "complete", "incomplete"

  // NEW: controls visibility of the "Add Patient" button
  const [canAddPatient, setCanAddPatient] = useState(false);
  const [assignments, setAssignments] = useState([]);

  // NEW: Bulk Complete Service state
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ completed: 0, total: 0 });
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (!packageId || !technicianId) {
      setError("Missing package or technician information.");
      setLoading(false);
      return;
    }
    fetchPatients();
    checkRegistrationPermission(); // NEW
  }, [packageId, technicianId]);

  const fetchPatients = async () => {
    try {
      const data = await fetchTechnicianPatients({ technicianId, packageId, campId });
      // Group services by patient
      const groupedPatients = groupPatientsByUniqueId(data);
      setPatients(groupedPatients);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Checks whether this technician is assigned "Registration" for the current camp
  const checkRegistrationPermission = async () => {
    try {
      const res = await fetch(
        `https://cbackend.xraidigital.com/api/technician/assignments/?technician_id=${technicianId}`
      );
      if (!res.ok) {
        throw new Error("Failed to fetch technician assignments.");
      }
      const data = await res.json();
      const assignmentList = data.assignments || [];

      setAssignments(assignmentList); // <-- new

      const hasRegistrationForCamp = assignmentList.some(
        (assignment) =>
          assignment.camp_id === campId &&
          typeof assignment.service_name === "string" &&
          assignment.service_name.trim().toLowerCase() === "registration"
      );

      setCanAddPatient(hasRegistrationForCamp);
    } catch (err) {
      console.error("Error checking registration permission:", err);
      setCanAddPatient(false);
    }
  };

  // NEW: Navigates to the Add Patient page
  const handleAddPatientClick = () => {
    navigate("/add-patient", {
      state: {
        technicianId,
        campId,
        packageId,
        packageName
      }
    });
  };

  // Group patients by unique_patient_id and combine their services
  const groupPatientsByUniqueId = (patientData) => {
    const grouped = {};
    
    patientData.forEach(patient => {
      const key = patient.unique_patient_id;
      
      if (!grouped[key]) {
        grouped[key] = {
          id: patient.id,
          name: patient.name,
          unique_patient_id: patient.unique_patient_id,
          excel_patient_id: patient.excel_patient_id,
          age: patient.age,
          services: []
        };
      }
      
      // Add service with completion status
      grouped[key].services.push({
        name: patient.services,
        completed: patient.completed
      });
    });
    
    return Object.values(grouped);
  };


  // Calculate completion statistics for each patient
const getPatientStats = (services) => {
  const total = services.length;
  const completed = services.filter(service => service.completed).length;
  return { completed, total, percentage: Math.round((completed / total) * 100) };
};

   const filteredPatients = patients
     .filter(patient => {
       // First apply search filter
       const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         patient.excel_patient_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         patient.unique_patient_id.toLowerCase().includes(searchTerm.toLowerCase());
       
       if (!matchesSearch) return false;
       
       // Then apply status filter
       const stats = getPatientStats(patient.services);
       
       if (filterStatus === "complete") {
         return stats.completed === stats.total;
       } else if (filterStatus === "incomplete") {
         return stats.completed < stats.total;
       }
       return true; // "all"
     });
   
     
  const getServiceRoute = (service) => {
    const normalized = service.trim().toLowerCase();
    console.log("Normalized Service:", normalized);

    const serviceRoutes = {
      'audiometry': '/audiometry',
      'ecg': '/ecg',
      'x-ray': '/xray',
      'pft': '/pft',
      'optometry': '/optometry',
      'doctor consultation': '/doctor-consultation',
      'pathology': '/pathology',
      'dental consultation': '/dental-consultation',
      'vitals': '/vitals',
      'form 7': '/form7',
      'bmd': '/bmd',
      'tetanus vaccine': '/tetanus-vaccine',
      'typhoid vaccine': '/typhoid-vaccine',
      'coordinator': '/coordinator',
      'cbc': '/cbc',
      'complete hemogram': '/complete-hemogram',
      'hemoglobin': '/hemoglobin',
      'urine routine': '/urine-routine',
      'stool examination': '/stool-examination',
      'lipid profile': '/lipid-profile',
      'kidney profile': '/kidney-profile',
      'lft': '/lft',
      'kft': '/kft',
      'random blood glucose': '/random-blood-glucose',
      'blood grouping': '/blood-grouping',
      'tata1mg optometry': '/tata1mg-optometry',
      'dietician consultation': '/dietician-consultation',
      'dieticianconsultation': '/dietician-consultation',
      'vitals with pathology': '/vitals-with-pathology',
      'doctor consultation with dc': '/doctor-consultation-with-dc',
    };

    return serviceRoutes[normalized] || null;
  };

  const handleServiceClick = async (serviceName, patient, isCompleted) => {
    const trimmedService = serviceName.trim();
    const route = getServiceRoute(trimmedService);

    // Always fetch serviceId for all services
    let serviceId;
    try {
      serviceId = await fetchServiceIdByName(trimmedService);
    } catch (err) {
      alert(`Service ID not found for: "${trimmedService}"`);
      return;
    }

    const numericServiceId = Number(
      typeof serviceId === "object" && serviceId !== null ? (serviceId.id ?? serviceId.service_id) : serviceId
    );

    if (numericServiceId === 34) {
      const assignment = assignments.find(
        (a) => Number(a.service_id) === 34 && Number(a.camp_id) === Number(campId)
      );
      if (!assignment) {
        alert("Vitals with Pathology is not assigned to you for this camp.");
        return;
      }
      if (assignment.is_enabled !== true) {
        alert("Vitals with Pathology is disabled for this camp.");
        return;
      }
      navigate("/vitals-with-pathology", {
        state: {
          patientId: patient.unique_patient_id,
          patientName: patient.name,
          technicianId,
          campId: assignment.camp_id,
          campLocation: assignment.camp_location,
          packageId,
          packageName,
          assignmentId: assignment.assignment_id,
          serviceId: 34,
          serviceName: assignment.service_name,
          isEnabled: assignment.is_enabled,
          isEditing: isCompleted,
        },
      });
      return;
    }
    if (numericServiceId === 35) {
      const assignment = assignments.find(
        (a) => Number(a.service_id) === 35 && Number(a.camp_id) === Number(campId)
      );
      if (!assignment) {
        alert("Doctor Consultation with DC is not assigned to you for this camp.");
        return;
      }
      if (assignment.is_enabled !== true) {
        alert("Doctor Consultation with DC is disabled for this camp.");
        return;
      }
      navigate("/doctor-consultation-with-dc", {
        state: {
          patientId: patient.unique_patient_id,
          patientExcelId: patient.excel_patient_id,
          patientName: patient.name,
          technicianId,
          campId: assignment.camp_id,
          campLocation: assignment.camp_location,
          packageId,
          packageName,
          assignmentId: assignment.assignment_id,
          serviceId: 35,
          serviceName: assignment.service_name,
          isEnabled: assignment.is_enabled,
          isEditing: isCompleted,
        },
      });
      return;
    }
    // BLS Training (36), Nurse Training (37), Spectacle (38): mark-as-done only, no form
    if ([36, 37, 38].includes(numericServiceId)) {
      const assignment = assignments.find(
        (a) => Number(a.service_id) === numericServiceId && Number(a.camp_id) === Number(campId)
      );
      if (!assignment) {
        alert(`${trimmedService} is not assigned to you for this camp.`);
        return;
      }
      if (assignment.is_enabled !== true) {
        alert(`${assignment.service_name} is disabled for this camp.`);
        return;
      }
      if (isCompleted) {
        alert(`${assignment.service_name} is already marked as done for ${patient.name}.`);
        return;
      }
      if (window.confirm(`Mark "${assignment.service_name}" as done for ${patient.name}?`)) {
        try {
          await submitTechnicianServiceDone({
            patientId: patient.unique_patient_id,
            technicianId,
            serviceId, // same value your existing mark-done code passes
          });
          alert(`${assignment.service_name} marked as done!`);
          fetchPatients(); // refresh completion status
        } catch (err) {
          alert("Error: " + err.message);
        }
      }
      return; // never navigate for these services
    }


    // Special handling for ECG, X-ray, and PFT (services that are marked as done without forms)
    if (["ecg", "x-ray", "pft",'vaccine','blood'].includes(trimmedService.toLowerCase())) {
      if (isCompleted) {
        // Allow editing/unmarking completed services
        if (window.confirm("This service is already completed. Do you want to mark it as incomplete?")) {
          try {
            // You might need to implement an API call to unmark service as done
            // For now, we'll just refresh to show current status
            alert("Service status updated!");
            fetchPatients();
          } catch (err) {
            alert("Error: " + err.message);
          }
        }
      } else {
        // Mark as completed
        if (window.confirm("You want to mark this service as done?")) {
          try {
            await submitTechnicianServiceDone({
              patientId: patient.unique_patient_id,
              technicianId,
              serviceId
            });
            alert("Service marked as done!");
            // Refresh the patient list to update completion status
            fetchPatients();
          } catch (err) {
            alert("Error: " + err.message);
          }
        }
      }
      return; // Do not navigate for these services
    }

    if (!route) {
      alert(`No route defined for service: "${trimmedService}"`);
      return;
    }

    // For all other services (with forms), allow navigation regardless of completion status
    // This makes completed services editable
    navigate(route, {
      state: {
        patientId: patient.unique_patient_id,
        patientName: patient.name,
        technicianId: technicianId,
        serviceId: serviceId,
        isEditing: isCompleted // Pass this flag to indicate if we're editing a completed service
      }
    });
  };

  const generateQRCode = (patientId) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=PATIENT_${patientId}`;

  // ===== Bulk Complete Service helpers =====
  const hasIncompleteServices = (patient) =>
    patient.services.some(service => !service.completed);

  const togglePatientSelection = (patientId) => {
    setSelectedPatients(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredPatients
      .filter(patient => hasIncompleteServices(patient))
      .map(patient => patient.unique_patient_id);
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every(id => selectedPatients.includes(id));
    if (allSelected) {
      setSelectedPatients(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedPatients(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const clearSelection = () => {
    setSelectedPatients([]);
    setSelectedService("");
  };

  // Services that are still incomplete for at least one selected patient
  const getAvailableBulkServices = () => {
    const servicesSet = new Set();
    selectedPatients.forEach(patientId => {
      const patient = patients.find(p => p.unique_patient_id === patientId);
      if (!patient) return;
      patient.services.forEach(service => {
        if (!service.completed) {
          servicesSet.add(service.name.trim());
        }
      });
    });
    return Array.from(servicesSet);
  };

  const handleBulkComplete = async () => {
    if (selectedPatients.length === 0) {
      alert("Please select at least one patient.");
      return;
    }
    if (!selectedService) {
      alert("Please select a service.");
      return;
    }

    const patientsToProcess = selectedPatients
      .map(id => patients.find(p => p.unique_patient_id === id))
      .filter(Boolean);

    const normalizedService = selectedService.trim().toLowerCase();
    const serviceAssigned = patientsToProcess.some(patient =>
      patient.services.some(s => s.name.trim().toLowerCase() === normalizedService)
    );
    if (!serviceAssigned) {
      alert(`None of the selected patients have "${selectedService}" assigned.`);
      return;
    }
    const hasPending = patientsToProcess.some(patient =>
      patient.services.some(s => s.name.trim().toLowerCase() === normalizedService && !s.completed)
    );
    if (!hasPending) {
      alert("All selected patients have already completed this service.");
      return;
    }

    const confirmed = window.confirm(
      `You are about to mark\n\nService:\n${selectedService}\n\nPatients:\n${patientsToProcess.length}\n\nContinue?`
    );
    if (!confirmed) return;

    setBulkLoading(true);
    setBulkProgress({ completed: 0, total: patientsToProcess.length });
    setBulkResult(null);

    let success = 0;
    let failed = 0;
    let skipped = 0;

    let serviceId;
    try {
      serviceId = await fetchServiceIdByName(selectedService);
    } catch (err) {
      setBulkLoading(false);
      alert(`Service ID not found for: "${selectedService}"`);
      return;
    }

    for (const patient of patientsToProcess) {
      const serviceEntry = patient.services.find(
        s => s.name.trim().toLowerCase() === normalizedService
      );
      if (!serviceEntry || serviceEntry.completed) {
        skipped += 1;
      } else {
        try {
          await submitTechnicianServiceDone({
            patientId: patient.unique_patient_id,
            technicianId,
            serviceId
          });
          success += 1;
        } catch (err) {
          failed += 1;
        }
      }
      setBulkProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
    }

    setBulkLoading(false);
    setBulkResult({ success, failed, skipped });
    clearSelection();
    fetchPatients();
  };

  // Derived selection state
  const availableBulkServices = getAvailableBulkServices();
  const visibleIncompleteIds = filteredPatients
    .filter(patient => hasIncompleteServices(patient))
    .map(patient => patient.unique_patient_id);
  const allVisibleSelected =
    visibleIncompleteIds.length > 0 &&
    visibleIncompleteIds.every(id => selectedPatients.includes(id));
  const someVisibleSelected =
    visibleIncompleteIds.some(id => selectedPatients.includes(id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600 text-lg">Loading patient data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="bg-white border border-red-300 p-6 rounded shadow">
          <p className="text-red-800 font-semibold mb-2">Error</p>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Patients - {packageName || "Package"} (Camp #{campId})
          </h1>
          <div className="flex gap-2">
            {canAddPatient && (
              <button
                onClick={handleAddPatientClick}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                + Add Patient
              </button>
            )}
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              ← Back
            </button>
          </div>
        </div>
   
      <div className=" mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search patients by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setFilterStatus("incomplete")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === "incomplete"
                ? "bg-yellow-500 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Incomplete ({patients.filter(p => {
              const stats = getPatientStats(p.services);
              return stats.completed < stats.total;
            }).length})
          </button>
          
          <button
            onClick={() => setFilterStatus("complete")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === "complete"
                ? "bg-green-500 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Complete ({patients.filter(p => {
              const stats = getPatientStats(p.services);
              return stats.completed === stats.total;
            }).length})
          </button>
          
          <button
            onClick={() => setFilterStatus("all")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === "all"
                ? "bg-blue-500 text-white shadow-md"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            All ({patients.length})
          </button>
        </div>

        </div>

        {/* NEW: Bulk update result summary */}
        {bulkResult && !bulkLoading && (
          <div className="mb-6 bg-green-50 border border-green-300 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="font-semibold text-green-800">Bulk Update Completed</p>
              <p className="text-sm text-gray-700 mt-1">
                {bulkResult.success} Success · {bulkResult.failed} Failed · {bulkResult.skipped} Skipped
              </p>
            </div>
            <button
              onClick={() => setBulkResult(null)}
              className="text-green-700 text-sm font-medium hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* NEW: Bulk Complete toolbar */}
        {selectedPatients.length > 0 && (
          <div className="mb-6 bg-white border border-blue-200 shadow rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <span className="font-semibold text-blue-700">
                Selected Patients: {selectedPatients.length}
              </span>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Service:</label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  disabled={bulkLoading}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select service...</option>
                  {availableBulkServices.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
                <button
                  onClick={handleBulkComplete}
                  disabled={bulkLoading}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkLoading ? "Processing..." : "Complete Selected Service"}
                </button>
                <button
                  onClick={clearSelection}
                  disabled={bulkLoading}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Selection
                </button>
              </div>
            </div>
            {bulkLoading && (
              <div className="mt-4">
                <p className="text-sm text-gray-700">
                  Completed: {bulkProgress.completed} / {bulkProgress.total}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${bulkProgress.total ? (bulkProgress.completed / bulkProgress.total) * 100 : 0}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {filteredPatients.length === 0 ? (
            <div className="bg-white border border-gray-300 p-6 rounded text-center text-gray-600">
              {searchTerm 
                ? "No patients found matching your search." 
                : filterStatus === "complete"
                  ? "No completed patients found."
                  : filterStatus === "incomplete"
                    ? "No incomplete patients found."
                    : "No patients found for this package."
              }
            </div>
          ): (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300 shadow rounded-lg">
              <thead className="bg-blue-100">
                <tr>
                  <th className="text-left px-4 py-3 border-b w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={visibleIncompleteIds.length === 0 || bulkLoading}
                      ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                      className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                      title="Select all incomplete patients"
                    />
                  </th>
                  <th className="text-left px-4 py-3 border-b">#</th>
                  <th className="text-left px-4 py-3 border-b">Patient Name</th>
                  <th className="text-left px-4 py-3 border-b">Patient ID</th>
                  <th className="text-left px-4 py-3 border-b">Age</th> 
                  <th className="text-left px-4 py-3 border-b">Progress</th>
                  <th className="text-left px-4 py-3 border-b">Services</th>
                  
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient, index) => {
                  const stats = getPatientStats(patient.services);
                  return (
                    <tr key={patient.unique_patient_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border-b">
                        <input
                          type="checkbox"
                          checked={selectedPatients.includes(patient.unique_patient_id)}
                          onChange={() => togglePatientSelection(patient.unique_patient_id)}
                          disabled={!hasIncompleteServices(patient) || bulkLoading}
                          className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                          title={hasIncompleteServices(patient)
                            ? "Select patient"
                            : "No incomplete services"}
                        />
                      </td>
                      <td className="px-4 py-3 border-b">{index + 1}</td>
                      <td className="px-4 py-3 border-b font-medium text-gray-900">{patient.name}</td>
                      <td className="px-4 py-3 border-b font-mono text-sm">{patient.excel_patient_id || "—"}</td>
                      <td className="px-4 py-3 border-b text-center font-semibold">
                        {patient.age ?? "—"}   {/* ✅ AGE */}
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center space-x-2">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            stats.completed === stats.total 
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : stats.completed === 0
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          }`}>
                            {stats.completed === stats.total ? (
                              <>
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                Complete
                              </>
                            ) : stats.completed === 0 ? (
                              <>
                                <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                Not Started
                              </>
                            ) : (
                              <>
                                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                                In Progress
                              </>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {stats.completed}/{stats.total}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex flex-wrap gap-1">
                          {patient.services.map((service, i) => {
                            const trimmedService = service.name.trim();
                            const route = getServiceRoute(trimmedService);
                            const isCompleted = service.completed;
                            
                            return (
                              <span
                                key={i}
                                title={
                                  isCompleted 
                                    ? route 
                                      ? "Service completed - Click to edit" 
                                      : "Service completed - Click to manage"
                                    : route 
                                      ? "Click to open service form" 
                                      : "Form not available"
                                }
                                className={`text-xs px-2 py-1 rounded cursor-pointer relative transition-all duration-200 ${
                                  isCompleted
                                    ? 'bg-green-500 text-white hover:bg-green-600 shadow-md'
                                    : route
                                      ? 'bg-blue-200 text-blue-900 hover:bg-blue-300'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => handleServiceClick(trimmedService, patient, isCompleted)}
                              >
                                {trimmedService}
                                {isCompleted && (
                                  <span className="absolute -top-1 -right-1 bg-green-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs shadow-sm">
                                    ✓
                                  </span>
                                )}
                                {isCompleted && route && (
                                  <span className="absolute -top-1 -left-1 bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs shadow-sm">
                                    ✎
                                  </span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PatientTechnicianDashboard;